import { t, UUID, Money, DateTimeISO } from '../common.ts';
import { BASE_TRANSACTION_TYPES, LEDGER_TRANSACTION_TYPES } from './wallet-tx-types.ts';

export const WalletView = t.Object(
  {
    memberId: UUID,
    creditCash: Money,
    creditPromotion: Money,
    commissionWallet: Money,
    cashbackWallet: Money,
    /** The fifth pool. Reported on its own; `balance` and `withdrawable` both exclude it. */
    freegameWallet: Money,
    balance: Money,
    withdrawable: Money,
    updatedAt: DateTimeISO,
  },
  { $id: 'WalletView' },
);

/**
 * The vocabulary a ledger row's `type` is drawn from, built from
 * `./wallet-tx-types.ts` rather than retyped here. That module is the one
 * definition, and it is the one a browser bundle can read without pulling
 * TypeBox in; this is its wire shape.
 *
 * `TransactionType` is the BASE words. `LedgerTransactionType` is what a row can
 * actually carry, which is the base words plus a `_compensate` reversal of each,
 * so a `TransactionView` describing a compensation row still validates.
 */
export const TransactionType = t.Union(
  BASE_TRANSACTION_TYPES.map((type) => t.Literal(type)),
);

export const LedgerTransactionType = t.Union(
  LEDGER_TRANSACTION_TYPES.map((type) => t.Literal(type)),
);

export const TransactionView = t.Object(
  {
    id: UUID,
    memberId: UUID,
    category: t.Union([t.Literal('finance'), t.Literal('bonus'), t.Literal('bet'), t.Literal('admin'), t.Literal('system')]),
    type: LedgerTransactionType,
    cashDelta: t.Integer(),
    promoDelta: t.Integer(),
    cashBefore: t.Integer(),
    cashAfter: t.Integer(),
    promoBefore: t.Integer(),
    promoAfter: t.Integer(),
    totalDelta: t.Integer(),
    refTable: t.Union([t.String(), t.Null()]),
    refId: t.Union([UUID, t.Null()]),
    parentTxId: t.Union([UUID, t.Null()]),
    idempotencyKey: t.Union([t.String(), t.Null()]),
    reason: t.Union([t.String(), t.Null()]),
    byAdminId: t.Union([UUID, t.Null()]),
    status: t.Union([t.Literal('pending'), t.Literal('applied'), t.Literal('cancelled'), t.Literal('reverted')]),
    createdAt: DateTimeISO,
  },
  { $id: 'TransactionView' },
);

/**
 * The alphabet and length budget of a CALLER-SUPPLIED ledger idempotency key.
 *
 * Every route that lets an outside caller name a `wallet.transactions` row
 * shares this field. The column is read back by two audiences — the player, on
 * their own transaction projection (`toPlayerTxView`), and a backoffice
 * operator, on the un-projected view — so whatever a caller puts here lands on
 * someone else's screen. Bounding the character class to ASCII identifier
 * punctuation is what keeps Thai prose, spaces, markup and script out of both,
 * and `maxLength` keeps the value inside the 128-char column (`deriveLegKey`
 * budgets its leg suffix against the same number).
 *
 * This bounds the CHARACTER CLASS only, and the class is not the confidentiality
 * boundary: `refund-for-somchai` is inside it. Requiring the key to be a machine
 * key — a registered namespace AND that producer's tail grammar — is a separate,
 * stricter rule applied by `POST /wallets/:memberId/adjust` and by the three
 * pool actions, via `isMachineLedgerKey` in `@b1fr0st/shared` (#177). See that
 * helper's doc block for why `POST /wallet/internal/tx` deliberately applies
 * neither rule.
 */
/**
 * Longest key a ledger posting is booked under on the wallet's internal routes,
 * and the longest ref a caller names a posting by (a refund's `refundsRef`, a
 * compensation's `idempotencyKey`).
 *
 * svc-game books a seamless posting under `seamless:<provider uuid>:<txId>`, 46
 * characters before the txId, and an AWC txId carries the bet id and AWC's
 * clock: `unvoidsettle:<bet>:<updateTime>`. With the 128-character bet id and
 * 40-character `updateTime` the AWC adapter accepts, that key is 228 and its
 * batch-rollback refund ref (`<key>:comp`) 233.
 */
export const WALLET_POSTING_KEY_MAX = 256;

/**
 * Longest ref a correction is recorded under: a refund's `refundRef` and a
 * compensation's `correctionRef`, kept whole in `ref_id`.
 */
export const WALLET_CORRECTION_REF_MAX = 256;

export const LedgerIdempotencyKey = t.String({
  minLength: 1,
  maxLength: 128,
  pattern: '^[A-Za-z0-9_:.-]+$',
});

export const AdminAdjustRequest = t.Object(
  {
    memberId: UUID,
    pool: t.Union([t.Literal('cash'), t.Literal('promotion')]),
    op: t.Union([t.Literal('add'), t.Literal('sub')]),
    amount: Money,
    /**
     * Audit note. REQUIRED by the service (a blank one is refused), and left
     * optional here on purpose: a schema cannot tell an absent note from a
     * string of spaces, so one rule enforced in one place answers both with the
     * same refusal instead of two different ones for the same mistake.
     *
     * Unbounded below for the same reason it always was: a minimum LENGTH only
     * ever bought filler. Requiring the field and requiring a paragraph are
     * different rules, and only the first is kept.
     */
    reason: t.Optional(t.String({ maxLength: 500 })),
    memberBankAccountId: t.Optional(UUID),
    /**
     * The operator's own house account the cash moved through — the deposit
     * account the money landed in on an `add`, the withdraw account it was paid
     * out of on a `sub`. Recorded for reconciliation against the bank statement,
     * which the member's own account (above) cannot answer for: a manual
     * adjustment is settled on the house side.
     */
    houseBankAccountId: t.Optional(UUID),
    /**
     * Wagering the member owes before this bonus principal may leave the
     * promotion pool, minor units. Only meaningful on `pool: 'promotion'` with
     * `op: 'add'`; the route refuses it anywhere else rather than accepting a
     * number it would then ignore.
     *
     * Bounded above by the safe-integer ceiling for the same reason `cols.money`
     * columns are: the value is read back as a JS number, and one above that
     * ceiling is stored lossily.
     */
    turnoverRequired: t.Optional(t.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER })),
    /**
     * Optional so existing callers keep working; the service mints one when it
     * is absent, which is what makes an adjustment reversible at all
     * (`compensateTransaction` looks the original up by key and by nothing
     * else). Supplying your own is the ONLY thing that dedups a repeated
     * request — a server-minted key differs every time by design (#132).
     *
     * The alphabet is `isValidIdempotencyKey`'s. It is the first of two bounds,
     * not the boundary itself: this column is on the player's own transaction
     * projection by design, so a key the operator types is a key the member
     * reads back, and an ASCII sentence clears the alphabet without trouble.
     * The route pairs it with `isMachineLedgerKey` (#177), which is what keeps
     * an operator name or an internal note out of the field.
     */
    idempotencyKey: t.Optional(LedgerIdempotencyKey),
  },
  { $id: 'AdminAdjustRequest' },
);

export const SeamlessBetRequest = t.Object(
  {
    externalTxId: t.String(),
    roundId: t.String(),
    memberId: UUID,
    providerId: UUID,
    gameCode: t.String(),
    amount: Money,
    currency: t.String({ default: 'THB' }),
    idempotencyKey: t.String(),
  },
  { $id: 'SeamlessBetRequest' },
);

export const SeamlessBetResponse = t.Object(
  {
    balance: Money,
    currency: t.String(),
    acceptedAt: DateTimeISO,
  },
  { $id: 'SeamlessBetResponse' },
);
